<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LabelItem from 'dashboard/components-next/label/LabelItem.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  // The card's label titles.
  selected: { type: Array, required: true },
  // The account's labels: { id, title, color }.
  labels: { type: Array, required: true },
});

const emit = defineEmits(['toggle']);

const { t } = useI18n();

const isMenuOpen = ref(false);
// Which chip shows its remove button; tracked in JS like upstream's contact labels, so the
// last chip of a line does not flicker on hover.
const hoveredLabel = ref(null);

const selectedLabels = computed(() =>
  props.labels.filter(label => props.selected.includes(label.title))
);

// Unselected first, so the labels still to add are at hand.
const labelMenuItems = computed(() =>
  props.labels
    .map(label => ({
      label: label.title,
      value: label.id,
      thumbnail: { name: label.title, color: label.color },
      isSelected: props.selected.includes(label.title),
      action: 'cardLabel',
    }))
    .toSorted((a, b) => Number(a.isSelected) - Number(b.isSelected))
);

const toggleById = id =>
  emit('toggle', props.labels.find(label => label.id === id).title);
</script>

<!--
  Not upstream's AddLabel: its menu opens from the button's left edge, which runs off the
  narrow side panel whenever the button wraps to the end of a line. This menu opens from the
  far edge of the whole list instead.
-->
<template>
  <div
    v-on-clickaway="() => (isMenuOpen = false)"
    class="relative flex flex-wrap items-center gap-1.5"
    @mouseleave="hoveredLabel = null"
  >
    <LabelItem
      v-for="label in selectedLabels"
      :key="label.id"
      :label="label"
      :is-hovered="hoveredLabel === label.id"
      @hover="hoveredLabel = label.id"
      @remove="toggleById($event.id)"
    />
    <button
      type="button"
      data-testid="card-label-add"
      class="flex items-center h-6 gap-1 px-2 py-1 rounded-md outline-dashed outline-1 outline-n-slate-6 hover:bg-n-alpha-2"
      :class="{ 'bg-n-alpha-2': isMenuOpen }"
      @click="isMenuOpen = !isMenuOpen"
    >
      <span class="i-lucide-plus" />
      <span class="text-sm text-n-slate-11">{{ t('LABEL.TAG_BUTTON') }}</span>
    </button>
    <DropdownMenu
      v-if="isMenuOpen"
      :menu-items="labelMenuItems"
      show-search
      class="z-50 w-48 mt-1 overflow-y-auto top-full max-h-52 ltr:right-0 rtl:left-0"
      @action="toggleById($event.value)"
    >
      <!-- A label's color is account data, so it cannot be a Tailwind class; upstream's
           LabelItem and AddLabel bind it the same way. -->
      <template #thumbnail="{ item }">
        <span
          class="flex-shrink-0 rounded-sm size-2"
          :style="{ backgroundColor: item.thumbnail.color }"
        />
      </template>
    </DropdownMenu>
  </div>
</template>
